import { useMemo, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import { procurementServices, fleetVehiclesServices } from '@services';
import { getCleanedUrlParams, showError } from '@utils';
import { GENERIC_ERROR_MESSAGE } from '@constants';

import { QUERY_KEYS } from 'constants/queryKeys';

import {
  PROCUREMENT_ORDERS_PAGE_SIZE,
  PROCUREMENT_ORDERS_DEFAULT_ORDER,
  PROCUREMENT_ORDERS_DEFAULT_ORDER_BY,
} from 'components/procurement/constants';
import { useSelector } from 'react-redux';
import { selectProcurementPinnedFilters } from '@store';
import {
  handleProcurementOrdersData,
  handleSuppliersData,
  handleSourcingTypes,
  getProcurementOrdersPayload,
} from './utils';

const useProcurementOrdersOperations = (fetchOnMount = true) => {
  const { query, isReady } = useRouter();

  const {
    page = 1,
    pageSize = PROCUREMENT_ORDERS_PAGE_SIZE,
    order = PROCUREMENT_ORDERS_DEFAULT_ORDER,
    orderBy = PROCUREMENT_ORDERS_DEFAULT_ORDER_BY,
    query: searchQuery,
    ...rest
  } = query;

  const pinnedFilters = useSelector(selectProcurementPinnedFilters);
  const hasFilters = pinnedFilters?.length > 0 || Object.keys(rest).length > 0;

  const {
    data: procurementOrdersData,
    isLoading: isProcurementOrdersLoading,
    isError: isProcurementOrdersError,
    error: procurementOrdersError,
    refetch: refetchProcurementOrders,
  } = useQuery(
    [QUERY_KEYS.GET_PROCUREMENT_ORDERS, query],
    () => {
      const params = {
        page,
        pageSize: parseInt(pageSize, 10) || PROCUREMENT_ORDERS_PAGE_SIZE,
        order,
        orderBy,
        query: searchQuery,
      };

      const cleanedParams = getCleanedUrlParams(params);
      const payload = getProcurementOrdersPayload(query);

      return procurementServices.getProcurementOrders({
        params: cleanedParams,
        payload,
      });
    },
    {
      enabled: isReady && fetchOnMount,
    }
  );

  const enumsResponse = useQuery([QUERY_KEYS.GET_PROCUREMENT_ENUMS_KEY], () =>
    procurementServices.getProcurementEnums()
  );
  const procurementOrders = useMemo(
    () => handleProcurementOrdersData(procurementOrdersData?.data),
    [procurementOrdersData?.data]
  );

  const total = procurementOrdersData?.meta?.total;

  const {
    data: suppliersData,
    isLoading: isSuppliersLoading,
    isError: isSuppliersError,
    error: suppliersError,
  } = useQuery(
    [QUERY_KEYS.GET_PROCUREMENT_ORDERS_SUPPLIERS],
    () => procurementServices.getProcurementOrdersSuppliers(),
    {
      enabled: isReady,
      refetchOnMount: false,
    }
  );

  const suppliers = useMemo(
    () => handleSuppliersData(suppliersData?.data),
    [suppliersData]
  );

  const {
    data: sourcingTypesData,
    isLoading: isSourcingTypesLoading,
    isError: isSourcingTypesError,
    error: SourcingTypesError,
  } = useQuery(
    [QUERY_KEYS.GET_FLEET_SOURCING_TYPES],
    () => fleetVehiclesServices.getVehicleSourcingTypes(),
    {
      enabled: true,
      refetchOnMount: false,
    }
  );

  const sourcingTypes = useMemo(
    () => handleSourcingTypes(sourcingTypesData?.data),
    [sourcingTypesData]
  );

  const hasErrors =
    isProcurementOrdersError || isSuppliersError || isSourcingTypesError;

  const errorMessage =
    procurementOrdersError || suppliersError || SourcingTypesError || hasErrors
      ? GENERIC_ERROR_MESSAGE
      : null;

  useEffect(() => {
    if (errorMessage) {
      showError(errorMessage);
    }
  }, [errorMessage]);

  return {
    total,
    loading: isProcurementOrdersLoading || isSuppliersLoading,
    procurementOrders,
    suppliers,
    sourcingTypes,
    isSourcingTypesLoading,
    page,
    enums: enumsResponse?.data?.data || {},
    pageSize: parseInt(pageSize, 10) || PROCUREMENT_ORDERS_PAGE_SIZE,
    getProcurementOrders: refetchProcurementOrders,
    hasFilters,
  };
};

export default useProcurementOrdersOperations;
